import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API } from '../auth';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { Plus, Trash2, MessageSquare, Building2 } from 'lucide-react';
import { toast } from 'sonner';

export default function CRMPage() {
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [notes, setNotes] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ note: '', note_type: 'general' });

  useEffect(() => {
    axios.get(`${API}/companies`).then(r => {
      setCompanies(r.data);
      if (r.data.length > 0) selectCompany(r.data[0]);
    });
  }, []);

  const selectCompany = async (c) => {
    setSelectedCompany(c);
    const res = await axios.get(`${API}/notes/company/${c.id}`);
    setNotes(res.data);
  };

  const handleCreate = async () => {
    try {
      await axios.post(`${API}/notes`, { ...form, company_id: selectedCompany.id });
      toast.success('Заметка добавлена');
      setDialogOpen(false);
      setForm({ note: '', note_type: 'general' });
      selectCompany(selectedCompany);
    } catch (err) {
      toast.error('Ошибка');
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/notes/${id}`);
      selectCompany(selectedCompany);
      toast.success('Удалено');
    } catch (err) {
      toast.error('Ошибка');
    }
  };

  const typeLabels = {
    general: '📝 Общее',
    call: '📞 Звонок',
    meeting: '🤝 Встреча',
    complaint: '⚠️ Жалоба',
  };

  return (
    <div className="space-y-6" data-testid="crm-page">
      <div>
        <h1 className="text-3xl font-bold text-gray-900 tracking-tight">CRM - Заметки по клиентам</h1>
        <p className="text-gray-500 mt-1">История общения с клиентами</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Companies List */}
        <Card className="p-4 lg:col-span-1">
          <h3 className="font-semibold text-gray-900 mb-3">Клиенты</h3>
          <div className="space-y-1 max-h-[500px] overflow-y-auto">
            {companies.map(c => (
              <button
                key={c.id}
                onClick={() => selectCompany(c)}
                data-testid={`select-company-${c.client_code}`}
                className={`w-full text-left p-3 rounded-md transition-colors ${
                  selectedCompany?.id === c.id ? 'bg-[#1A1A1A] text-white' : 'hover:bg-gray-100'
                }`}
              >
                <div className="flex items-center gap-2">
                  <Building2 className="h-4 w-4 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm truncate">{c.company_name}</p>
                    <p className={`text-xs font-mono ${selectedCompany?.id === c.id ? 'text-orange-100' : 'text-gray-500'}`}>{c.client_code}</p>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </Card>

        {/* Notes */}
        <div className="lg:col-span-2 space-y-4">
          {selectedCompany && (
            <Card className="p-5">
              <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
                <div>
                  <p className="text-xs text-gray-500 font-mono">{selectedCompany.client_code}</p>
                  <h2 className="text-xl font-semibold">{selectedCompany.company_name}</h2>
                  <p className="text-sm text-gray-500">{selectedCompany.contact_person} · {selectedCompany.phone}</p>
                </div>
                <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                  <DialogTrigger asChild>
                    <Button className="cff-button-primary" data-testid="add-note-btn">
                      <Plus className="h-4 w-4 mr-2" /> Добавить заметку
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader><DialogTitle>Новая заметка</DialogTitle></DialogHeader>
                    <div className="space-y-3 py-2">
                      <div>
                        <Label>Тип</Label>
                        <Select value={form.note_type} onValueChange={(v) => setForm({...form, note_type: v})}>
                          <SelectTrigger data-testid="note-type-select"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {Object.entries(typeLabels).map(([k, v]) => (
                              <SelectItem key={k} value={k}>{v}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Текст заметки</Label>
                        <Textarea
                          value={form.note}
                          onChange={(e) => setForm({...form, note: e.target.value})}
                          rows={5}
                          placeholder="Например: Клиент попросил скидку 10% за объём от 50 коробок..."
                          data-testid="note-textarea"
                        />
                      </div>
                    </div>
                    <DialogFooter>
                      <Button variant="outline" onClick={() => setDialogOpen(false)}>Отмена</Button>
                      <Button onClick={handleCreate} className="cff-button-primary" data-testid="save-note-btn">Сохранить</Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </div>

              {notes.length === 0 ? (
                <div className="text-center py-12 text-gray-400">
                  <MessageSquare className="h-12 w-12 mx-auto mb-2 opacity-30" />
                  <p>Нет заметок по этому клиенту</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {notes.map(n => (
                    <div key={n.id} className="border border-gray-200 rounded-md p-3" data-testid={`note-${n.id}`}>
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <span className="text-xs px-2 py-0.5 bg-gray-100 rounded">{typeLabels[n.note_type] || n.note_type}</span>
                        <button onClick={() => handleDelete(n.id)} className="text-red-500 hover:text-red-700">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                      <p className="text-sm text-gray-700 whitespace-pre-wrap">{n.note}</p>
                      <p className="text-xs text-gray-400 mt-2 cff-text-numeric">
                        {new Date(n.created_at).toLocaleString('ru-RU')}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
